import React, { createContext, ReactNode, useContext, useMemo } from "react";
import type * as client from "../../client";
import { useCRMContacts } from "../../pages/specialty-assistant/data";

type CRMContactsContextValue = {
  // Emails are normalized to lower-case keys to avoid case-sensitivity issues.
  contactsByEmail: Map<string, client.ReportContact>;
  isPending: boolean;
  refetch: (() => Promise<unknown>) | undefined;
};

const CRMContactsContext = createContext<CRMContactsContextValue | undefined>(
  undefined
);

export const CRMContactsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const query = useCRMContacts();
  const value: CRMContactsContextValue = useMemo(() => {
    const arr = query.data?.contacts ?? [];
    const map = new Map<string, client.ReportContact>();
    for (const c of arr) {
      if (c.email) {
        map.set(c.email.toLowerCase(), c);
      }
    }

    return {
      contactsByEmail: map,
      isPending: query.isPending,
      refetch: query.refetch,
    };
  }, [query.data?.contacts, query.isPending, query.refetch]);

  return (
    <CRMContactsContext.Provider value={value}>
      {children}
    </CRMContactsContext.Provider>
  );
};

export const useCRMContactsContext = (): CRMContactsContextValue => {
  const ctx = useContext(CRMContactsContext);
  if (!ctx) {
    throw new Error(
      "useCRMContactsContext must be used within CRMContactsProvider"
    );
  }
  return ctx;
};
